import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, ExternalLink, Calculator, MessageCircle } from "lucide-react";

import { useSite } from "@/contexts/SiteContext";

const NAV = [
  { label: "Home", to: "/" },
  {
    label: "About Us",
    children: [
      { label: "Our Company", to: "/our-company" },
      { label: "Our Advisory Team", to: "/our-advisory-team" },
      { label: "Why PIRAC", to: "/why-pirac" },
    ],
  },
  { label: "Framework", to: "/framework" },
  {
    label: "Success Stories",
    children: [
      { label: "Case Studies", to: "/case-studies" },
      { label: "Testimonials", to: "/testimonials" },
      // Awards & Recognition is intentionally absent from the main navigation.
      // The page stays published and linked from the footer — the client asked for
      // it to be de-emphasised for now, not removed, so restoring it is one line.
    ],
  },
  {
    label: "Resources",
    children: [
      // Property Valuation was promoted out of this dropdown into its own
      // button in the bar — it is a primary action, not a resource link.
      { label: "Calculators Hub", to: "/calculators" },
      { label: "· Mortgage Calculator", to: "/calculators/mortgage" },
      { label: "· Sales Proceeds", to: "/calculators/sales-proceeds" },
      { label: "· Purchase Calculator", to: "/calculators/purchase" },
      { label: "Blogs & Insights", to: "/blogs" },
    ],
  },
  { label: "Contact", to: "/contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const loc = useLocation();

  // From Site Settings, so the number is corrected in one place rather than in
  // five hardcoded links across the header, homepage and footer.
  const { site } = useSite();
  const whatsapp = (site.whatsappNumber || "").replace(/\D/g, "");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenMenu(null);
  }, [loc.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background,border] duration-500 ${
        scrolled
          ? "bg-[#060f1e]/85 backdrop-blur-xl border-b border-[#1a2940]"
          : "bg-[#060f1e]/40 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="flex items-center justify-between h-20 lg:h-24">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group shrink-0" data-testid="header-logo">
            <img
              src="/assets/pirac-logo.webp"
              alt="PIRAC — Property Investment Research and Advisory Centre"
              className="h-12 w-12 lg:h-14 lg:w-14 object-cover"
              style={{ clipPath: "inset(6% 6% 6% 6%)" }}
            />
            <div className="hidden sm:block leading-tight">
              <div className="font-serif text-xl lg:text-2xl tracking-wider text-[#f7f5f0]">PIRAC</div>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden xl:flex items-center gap-0.5 2xl:gap-1 shrink-0" aria-label="Primary">
            {NAV.map((item) => (
              <NavItem key={item.label} item={item} openMenu={openMenu} setOpenMenu={setOpenMenu} />
            ))}
          </nav>

          {/*
            Three actions, in the order the client specified. Only "Book a
            Consultation" is filled — three solid buttons compete with each other
            and none of them then reads as the primary step. The WhatsApp label
            collapses to its icon below 2xl: at 1280px — a very common laptop
            width — six nav items and three full labels do not share a row, and
            the logo ends up overprinting the first nav link.
          */}
          <div className="hidden xl:flex items-center gap-2 2xl:gap-3 shrink-0">
            {whatsapp && (
              <a
                href={`https://wa.me/${whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-2 h-11 px-3 2xl:px-4 bg-[#cca24d] text-[#060f1e] text-sm font-medium tracking-wide hover:bg-[#e5c98b] transition-colors duration-300"
                data-testid="header-whatsapp"
                aria-label="WhatsApp PIRAC"
              >
                <MessageCircle className="h-4 w-4 shrink-0" strokeWidth={1.5} />
                <span className="hidden 2xl:inline">WhatsApp Us</span>
              </a>
            )}
            <Link
              to="/contact"
              data-testid="header-cta-book"
              className="group inline-flex items-center gap-2 px-4 2xl:px-5 h-11 bg-[#cca24d] text-[#060f1e] text-sm font-medium tracking-wide hover:bg-[#e5c98b] transition-colors duration-300 whitespace-nowrap"
            >
              Book a Consultation
              <span className="hidden 2xl:inline w-4 h-px bg-[#060f1e] transition-all duration-300 group-hover:w-6" />
            </Link>
            <Link
              to="/property-valuation"
              data-testid="header-cta-valuation"
              className="group inline-flex items-center gap-2 px-4 2xl:px-5 h-11 bg-[#cca24d] text-[#060f1e] text-sm font-medium tracking-wide hover:bg-[#e5c98b] transition-colors duration-300 whitespace-nowrap"
            >
              <Calculator className="h-4 w-4 shrink-0" strokeWidth={1.5} />
              <span className="2xl:hidden">Valuation</span>
              <span className="hidden 2xl:inline">Property Valuation</span>
            </Link>
          </div>

          {/* Mobile toggle */}
          <button
            onClick={() => setMobileOpen((v) => !v)}
            className="xl:hidden h-11 w-11 flex items-center justify-center text-[#f7f5f0]"
            data-testid="header-mobile-toggle"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="h-5 w-5" strokeWidth={1.5} /> : <Menu className="h-5 w-5" strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="xl:hidden bg-[#060f1e] border-t border-[#1a2940] overflow-hidden"
          >
            <div className="px-6 py-6 space-y-1">
              {NAV.map((item) => (
                <MobileNavItem key={item.label} item={item} />
              ))}
              {/* Stacked on mobile — three side-by-side buttons truncate. */}
              <div className="pt-6 space-y-3">
                {whatsapp && (
                  <a
                    href={`https://wa.me/${whatsapp}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full h-12 flex items-center justify-center gap-2 bg-[#cca24d] text-[#060f1e] text-sm font-medium"
                    data-testid="mobile-whatsapp"
                  >
                    <MessageCircle className="h-4 w-4" strokeWidth={1.5} /> WhatsApp Us
                  </a>
                )}
                <Link
                  to="/contact"
                  className="w-full h-12 flex items-center justify-center bg-[#cca24d] text-[#060f1e] text-sm font-medium"
                  data-testid="mobile-cta-book"
                >
                  Book a Consultation
                </Link>
                <Link
                  to="/property-valuation"
                  className="w-full h-12 flex items-center justify-center gap-2 bg-[#cca24d] text-[#060f1e] text-sm font-medium"
                  data-testid="mobile-cta-valuation"
                >
                  <Calculator className="h-4 w-4" strokeWidth={1.5} /> Property Valuation
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

function NavItem({ item, openMenu, setOpenMenu }) {
  const isOpen = openMenu === item.label;
  if (!item.children) {
    return (
      <NavLink
        to={item.to}
        end={item.to === "/"}
        className={({ isActive }) =>
          `px-2.5 2xl:px-4 h-10 flex items-center text-sm tracking-wide whitespace-nowrap transition-colors duration-300 ${
            isActive ? "text-[#cca24d]" : "text-[#f7f5f0]/85 hover:text-[#cca24d]"
          }`
        }
        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
      >
        {item.label}
      </NavLink>
    );
  }
  return (
    <div
      className="relative"
      onMouseEnter={() => setOpenMenu(item.label)}
      onMouseLeave={() => setOpenMenu(null)}
    >
      <button
        className={`px-2.5 2xl:px-4 h-10 flex items-center gap-1.5 text-sm tracking-wide whitespace-nowrap transition-colors duration-300 ${
          isOpen ? "text-[#cca24d]" : "text-[#f7f5f0]/85 hover:text-[#cca24d]"
        }`}
        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
      >
        {item.label}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} strokeWidth={1.5} />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.25 }}
            className="absolute top-full left-0 pt-2 min-w-[260px]"
          >
            <div className="bg-[#0c1728] border border-[#1a2940] backdrop-blur-xl py-3">
              {item.children.map((c) =>
                c.external ? (
                  <a
                    key={c.label}
                    href={c.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between px-5 py-2.5 text-sm text-[#f7f5f0]/85 hover:text-[#cca24d] hover:bg-[#060f1e] transition-colors"
                    data-testid={`nav-child-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <span>{c.label}</span>
                    <ExternalLink className="h-3 w-3" strokeWidth={1.5} />
                  </a>
                ) : (
                  <NavLink
                    key={c.label}
                    to={c.to}
                    className={({ isActive }) =>
                      `block px-5 py-2.5 text-sm ${
                        isActive ? "text-[#cca24d]" : "text-[#f7f5f0]/85 hover:text-[#cca24d]"
                      } hover:bg-[#060f1e] transition-colors`
                    }
                    data-testid={`nav-child-${c.label.toLowerCase().replace(/[·\s]+/g, "-")}`}
                  >
                    {c.label}
                  </NavLink>
                )
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function MobileNavItem({ item }) {
  const [open, setOpen] = useState(false);
  if (!item.children) {
    return (
      <NavLink
        to={item.to}
        className={({ isActive }) =>
          `block py-3 text-base border-b border-[#1a2940]/60 ${
            isActive ? "text-[#cca24d]" : "text-[#f7f5f0]"
          }`
        }
      >
        {item.label}
      </NavLink>
    );
  }
  return (
    <div className="border-b border-[#1a2940]/60">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between py-3 text-base text-[#f7f5f0]"
      >
        {item.label}
        <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="pb-3 pl-4 space-y-1">
              {item.children.map((c) =>
                c.external ? (
                  <a key={c.label} href={c.href} target="_blank" rel="noopener noreferrer" className="block py-2 text-sm text-[#d8d8d8]">
                    {c.label} ↗
                  </a>
                ) : (
                  <NavLink key={c.label} to={c.to} className="block py-2 text-sm text-[#d8d8d8]">
                    {c.label}
                  </NavLink>
                )
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
